import { Trash2 } from "lucide-react";
import * as React from "react";

import type { NodeType } from "@/api/workflows";
import { Accordion, AccordionSection } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { EntityMultiPicker } from "@/components/ui/entity-multi-picker";
import { EntityPicker } from "@/components/ui/entity-picker";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useWorkflowDesignerStore } from "@/stores/workflowDesigner";

export function PropertiesPanel(): JSX.Element {
  const selectedId = useWorkflowDesignerStore((s) => s.selectedNodeId);
  const node = useWorkflowDesignerStore((s) =>
    s.flowNodes.find((n) => n.id === selectedId) || null,
  );
  const updateNodeData = useWorkflowDesignerStore((s) => s.updateNodeData);
  const removeNode = useWorkflowDesignerStore((s) => s.removeNode);

  if (!node) {
    return (
      <aside className="w-80 shrink-0 border-l bg-background overflow-y-auto">
        <div className="px-3 py-2 border-b">
          <h2 className="text-xs font-semibold text-muted-foreground uppercase">
            Properties
          </h2>
        </div>
        <div className="p-6 text-sm text-muted-foreground text-center">
          Chọn một node để edit.
        </div>
      </aside>
    );
  }

  const updateConfig = (patch: Record<string, unknown>) => {
    updateNodeData(node.id, { config: { ...node.data.config, ...patch } });
  };

  return (
    <aside className="w-80 shrink-0 border-l bg-background overflow-y-auto">
      <div className="px-3 py-2 border-b sticky top-0 bg-background z-10 flex items-center justify-between">
        <div>
          <h2 className="text-xs font-semibold text-muted-foreground uppercase">
            {node.data.nodeType}
          </h2>
          <code className="text-[10px] text-muted-foreground">{node.id}</code>
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="size-7 text-destructive hover:text-destructive"
          onClick={() => {
            if (confirm(`Xoá node ${node.id}?`)) removeNode(node.id);
          }}
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>

      <div className="p-3">
        <Accordion>
          <AccordionSection
            id="general"
            title="General"
            subtitle={node.data.label || "(no label)"}
            defaultOpen
          >
            <Row label="Label">
              <Input
                value={node.data.label}
                onChange={(e) =>
                  updateNodeData(node.id, { label: e.target.value })
                }
                className="h-8 text-sm"
              />
            </Row>
            <div className="text-[10px] text-muted-foreground mt-2 flex justify-between">
              <span>Type</span>
              <code>{node.data.nodeType}</code>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1 flex justify-between">
              <span>Node id</span>
              <code className="truncate max-w-[160px]">{node.id}</code>
            </div>
          </AccordionSection>
          <AccordionSection
            id="config"
            title="Config"
            subtitle={
              Object.keys(node.data.config).length > 0
                ? `${Object.keys(node.data.config).length} keys`
                : "default"
            }
            defaultOpen
          >
            <NodeTypeEditor
              nodeType={node.data.nodeType}
              config={node.data.config}
              onUpdate={updateConfig}
            />
          </AccordionSection>
        </Accordion>
      </div>
    </aside>
  );
}

// ============================================================================
// Per-type editors
// ============================================================================
function NodeTypeEditor({
  nodeType,
  config,
  onUpdate,
}: {
  nodeType: NodeType;
  config: Record<string, unknown>;
  onUpdate: (patch: Record<string, unknown>) => void;
}): JSX.Element | null {
  if (nodeType === "llm") return <LLMEditor config={config} onUpdate={onUpdate} />;
  if (nodeType === "tool") return <ToolEditor config={config} onUpdate={onUpdate} />;
  if (nodeType === "form") return <FormEditor config={config} onUpdate={onUpdate} />;
  if (nodeType === "branch") return <BranchEditor config={config} onUpdate={onUpdate} />;
  if (nodeType === "human") return <HumanEditor config={config} onUpdate={onUpdate} />;
  if (nodeType === "notify") return <NotifyEditor config={config} onUpdate={onUpdate} />;
  if (nodeType === "delay") return <DelayEditor config={config} onUpdate={onUpdate} />;
  return null;
}

function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="block text-xs font-medium mb-1">{label}</label>
      {children}
      {hint ? <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p> : null}
    </div>
  );
}

interface EditorProps {
  config: Record<string, unknown>;
  onUpdate: (patch: Record<string, unknown>) => void;
}

function LLMEditor({ config, onUpdate }: EditorProps): JSX.Element {
  return (
    <div className="space-y-3">
      <Row
        label="Prompt"
        hint="Hỗ trợ {{ data.field }} interpolation từ workflow state"
      >
        <Textarea
          value={String(config.prompt ?? "")}
          onChange={(e) => onUpdate({ prompt: e.target.value })}
          rows={5}
          className="text-xs font-mono"
        />
      </Row>
      <Row label="System (optional)">
        <Textarea
          value={String(config.system ?? "")}
          onChange={(e) =>
            onUpdate({ system: e.target.value || null })
          }
          rows={2}
          className="text-xs"
        />
      </Row>
      <Row label="Provider (optional)" hint="Mặc định = settings.llm_provider">
        <Input
          value={String(config.provider ?? "")}
          onChange={(e) => onUpdate({ provider: e.target.value || null })}
          placeholder="anthropic / openai / google / ollama"
          className="h-8 text-sm"
        />
      </Row>
      <Row label="Model (optional)">
        <Input
          value={String(config.model ?? "")}
          onChange={(e) => onUpdate({ model: e.target.value || null })}
          placeholder="claude-sonnet-4-6"
          className="h-8 text-sm"
        />
      </Row>
      <Row label="Output key">
        <Input
          value={String(config.output_key ?? "llm_output")}
          onChange={(e) => onUpdate({ output_key: e.target.value })}
          className="h-8 text-sm"
        />
      </Row>
    </div>
  );
}

function ToolEditor({ config, onUpdate }: EditorProps): JSX.Element {
  return (
    <div className="space-y-3">
      <Row
        label="Tool name"
        hint="Phải đã register trong agent registry"
      >
        <Input
          value={String(config.tool_name ?? "")}
          onChange={(e) => onUpdate({ tool_name: e.target.value })}
          placeholder="search_partner / create_quotation / ..."
          className="h-8 text-sm font-mono"
        />
      </Row>
      <Row label="Args (JSON, hỗ trợ {{ var }})">
        <Textarea
          value={JSON.stringify(config.args ?? {}, null, 2)}
          onChange={(e) => {
            try {
              onUpdate({ args: JSON.parse(e.target.value) });
            } catch {
              // ignore invalid JSON
            }
          }}
          rows={4}
          className="text-xs font-mono"
        />
      </Row>
      <Row label="Output key">
        <Input
          value={String(config.output_key ?? "tool_output")}
          onChange={(e) => onUpdate({ output_key: e.target.value })}
          className="h-8 text-sm"
        />
      </Row>
    </div>
  );
}

function FormEditor({ config, onUpdate }: EditorProps): JSX.Element {
  const formCode = config.form_code ? String(config.form_code) : null;
  return (
    <div className="space-y-3">
      <Row label="Form" hint="Pick từ list forms đã publish.">
        <EntityPicker
          kind="form"
          value={formCode}
          onChange={(id) => onUpdate({ form_code: id ?? "" })}
          filters={{ status: "published" }}
          placeholder="Chọn form..."
        />
      </Row>
      <Row label="Form version (optional)" hint="Để trống = published version">
        <Input
          type="number"
          value={config.form_version != null ? String(config.form_version) : ""}
          onChange={(e) =>
            onUpdate({
              form_version: e.target.value ? Number(e.target.value) : null,
            })
          }
          className="h-8 text-sm"
        />
      </Row>
      <Row label="Output key">
        <Input
          value={String(config.output_key ?? "form_data")}
          onChange={(e) => onUpdate({ output_key: e.target.value })}
          className="h-8 text-sm"
        />
      </Row>
    </div>
  );
}

function BranchEditor({ config, onUpdate }: EditorProps): JSX.Element {
  return (
    <Row
      label="Condition (json-logic)"
      hint='vd: {">":[{"var":"amount"},50000000]}'
    >
      <Textarea
        value={JSON.stringify(config.condition ?? { "==": [1, 1] }, null, 2)}
        onChange={(e) => {
          try {
            onUpdate({ condition: JSON.parse(e.target.value) });
          } catch {
            // ignore
          }
        }}
        rows={4}
        className="text-xs font-mono"
      />
    </Row>
  );
}

function HumanEditor({ config, onUpdate }: EditorProps): JSX.Element {
  const approverIds = Array.isArray(config.approver_ids)
    ? (config.approver_ids as string[])
    : [];
  return (
    <div className="space-y-3">
      <Row label="Title">
        <Input
          value={String(config.title ?? "Phê duyệt")}
          onChange={(e) => onUpdate({ title: e.target.value })}
          className="h-8 text-sm"
        />
      </Row>
      <Row label="Approver label" hint="Hiển thị trên approval card (UI label)">
        <Input
          value={String(config.approver_label ?? "")}
          onChange={(e) => onUpdate({ approver_label: e.target.value })}
          placeholder="CFO / Manager"
          className="h-8 text-sm"
        />
      </Row>
      <Row label="Approvers" hint="Pick user từ Odoo (multi-select).">
        <EntityMultiPicker
          kind="odoo_user"
          value={approverIds}
          onChange={(ids) => onUpdate({ approver_ids: ids })}
          placeholder="Chọn người duyệt..."
        />
      </Row>
      <Row label="Allowed actions (CSV)">
        <Input
          value={
            Array.isArray(config.allow_actions)
              ? (config.allow_actions as string[]).join(",")
              : "approve,reject"
          }
          onChange={(e) =>
            onUpdate({
              allow_actions: e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          className="h-8 text-sm"
        />
      </Row>
      <Row label="Timeout hours">
        <Input
          type="number"
          value={config.timeout_hours != null ? String(config.timeout_hours) : ""}
          onChange={(e) =>
            onUpdate({
              timeout_hours: e.target.value ? Number(e.target.value) : null,
            })
          }
          className="h-8 text-sm"
        />
      </Row>
    </div>
  );
}

function NotifyEditor({ config, onUpdate }: EditorProps): JSX.Element {
  const channels = Array.isArray(config.channels)
    ? (config.channels as string[])
    : [];
  const templateCode = config.template_code ? String(config.template_code) : null;
  return (
    <div className="space-y-3">
      <Row label="Channels" hint="Multi-select từ channels đã cấu hình.">
        <EntityMultiPicker
          kind="notification_channel"
          value={channels}
          onChange={(ids) => onUpdate({ channels: ids })}
          placeholder="Chọn channels..."
        />
      </Row>
      <Row label="Template" hint="Pick từ templates đã cấu hình.">
        <EntityPicker
          kind="notification_template"
          value={templateCode}
          onChange={(id) => onUpdate({ template_code: id ?? "" })}
          placeholder="Chọn template..."
        />
      </Row>
      <p className="text-[10px] text-muted-foreground">
        Lưu ý: actual channel delivery (Celery worker) đến P3-S3.
      </p>
    </div>
  );
}

function DelayEditor({ config, onUpdate }: EditorProps): JSX.Element {
  return (
    <Row label="Duration (seconds)">
      <Input
        type="number"
        value={String(config.duration_seconds ?? 0)}
        onChange={(e) =>
          onUpdate({ duration_seconds: Number(e.target.value) })
        }
        className="h-8 text-sm"
      />
    </Row>
  );
}
